<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import countries from 'shared/constants/countries';
import CallRecordingSettings from './CallRecordingSettings.vue';
import WhatsAppHolidayRow from './WhatsAppHolidayRow.vue';
import {
  DAYS,
  RING_MODES,
  ROUTING_TARGETS,
  PRIORITIZATION_STRATEGIES,
  CALL_ICON_VISIBILITIES,
  CALLBACK_PERMISSION_STATUSES,
  buildCallingForm,
  buildCallingPayload,
  buildCallingFormFromMeta,
} from './callingSettingsHelpers';

const ELIGIBILITY_FLAGS = [
  'respectContactBlocklist',
  'onlyOnline',
  'excludeBusy',
  'excludeAutoOffline',
  'requireInboxAccess',
];

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    NextButton,
    NextSelect,
    NextInput,
    TagMultiSelectComboBox,
    TextArea,
    Spinner,
    CallRecordingSettings,
    WhatsAppHolidayRow,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      callingEnabled: this.inbox.provider_config?.calling_enabled || false,
      inboundCallsEnabled:
        this.inbox.provider_config?.inbound_calls_enabled !== false,
      permissionRequestBody:
        this.inbox.provider_config?.call_permission_request_body || '',
      form: buildCallingForm(this.inbox.provider_config?.calling_config),
      isUpdating: false,
      isFetchingFromMeta: false,
      isTogglingCalling: false,
      isTogglingInbound: false,
      DAYS,
      ELIGIBILITY_FLAGS,
    };
  },
  computed: {
    phoneNumber() {
      return (
        this.inbox.provider_config?.phone_number || this.inbox.phone_number
      );
    },
    countryOptions() {
      return countries.map(country => ({
        value: country.id,
        label: country.name,
      }));
    },
    routingTargetOptions() {
      return ROUTING_TARGETS.map(target => ({
        value: target,
        label: this.$t(
          `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_TARGETS.${target}`
        ),
      }));
    },
    ringModeOptions() {
      return RING_MODES.map(mode => ({
        value: mode,
        label: this.$t(
          `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.RING_MODES.${mode}`
        ),
      }));
    },
    prioritizationOptions() {
      return PRIORITIZATION_STRATEGIES.map(strategy => ({
        value: strategy,
        label: this.$t(
          `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.PRIORITIZATION.${strategy}`
        ),
      }));
    },
    callIconVisibilityOptions() {
      return CALL_ICON_VISIBILITIES.map(visibility => ({
        value: visibility,
        label: this.$t(
          `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALL_ICON_VISIBILITIES.${visibility}`
        ),
      }));
    },
    callbackPermissionOptions() {
      return CALLBACK_PERMISSION_STATUSES.map(status => ({
        value: status,
        label: this.$t(
          `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALLBACK_PERMISSIONS.${status}`
        ),
      }));
    },
  },
  watch: {
    'inbox.provider_config.calling_enabled'(val) {
      this.callingEnabled = val || false;
    },
    'inbox.provider_config.call_permission_request_body'(val) {
      this.permissionRequestBody = val || '';
    },
    'inbox.provider_config.inbound_calls_enabled'(val) {
      this.inboundCallsEnabled = val !== false;
    },
    'inbox.provider_config.calling_config'(val) {
      this.form = buildCallingForm(val);
    },
  },
  methods: {
    dayLabel(day) {
      return this.$t(`INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.DAYS.${day}`);
    },
    eligibilityLabel(flag) {
      return this.$t(
        `INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ELIGIBILITY.${flag}`
      );
    },
    addHoliday() {
      this.form.holidays.push({
        date: '',
        name: '',
        openTime: '09:00',
        closeTime: '17:00',
      });
    },
    removeHoliday(index) {
      this.form.holidays.splice(index, 1);
    },
    resetForm() {
      this.form = buildCallingForm(this.inbox.provider_config?.calling_config);
      this.permissionRequestBody =
        this.inbox.provider_config?.call_permission_request_body || '';
    },
    // Meta only answers for the fields it owns; the routing chain is ours, so it stays as it is.
    async fetchFromMeta() {
      this.isFetchingFromMeta = true;
      try {
        const { data } = await InboxesAPI.getMetaCallingSettings(this.inbox.id);
        if (data.meta_error) {
          useAlert(data.meta_error);
          return;
        }
        this.form = { ...this.form, ...buildCallingFormFromMeta(data.calling) };
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.FETCH_FROM_META_DONE')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isFetchingFromMeta = false;
      }
    },
    async handleInboundToggle(newValue) {
      if (this.isTogglingInbound) return;
      const previousValue = this.inboundCallsEnabled;
      this.inboundCallsEnabled = newValue;
      this.isTogglingInbound = true;
      try {
        await InboxesAPI.setInboundCalls(this.inbox.id, newValue);
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.inboundCallsEnabled = previousValue;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isTogglingInbound = false;
      }
    },
    async handleCallingToggle(newValue) {
      if (this.isTogglingCalling) return;
      const previousValue = this.callingEnabled;
      this.callingEnabled = newValue;
      this.isTogglingCalling = true;
      try {
        if (newValue) {
          await InboxesAPI.enableWhatsappCalling(this.inbox.id);
        } else {
          await InboxesAPI.disableWhatsappCalling(this.inbox.id);
        }
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.callingEnabled = previousValue;
        const fallbackKey = newValue
          ? 'INBOX_MGMT.WHATSAPP_CALLING.ENABLE_FAILED'
          : 'INBOX_MGMT.EDIT.API.ERROR_MESSAGE';
        useAlert(this.$t(fallbackKey));
      } finally {
        this.isTogglingCalling = false;
      }
    },
    // The permission body lives in provider_config next to the credentials, so it keeps going
    // through the inbox update; the calling config has its own endpoint, which merges it in.
    async updateCallingSettings() {
      this.isUpdating = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              call_permission_request_body:
                this.permissionRequestBody.trim() || null,
            },
          },
        });
        await InboxesAPI.setCallingConfig(
          this.inbox.id,
          buildCallingPayload(this.form)
        );
        await this.$store.dispatch('inboxes/get', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(message);
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingCalling }"
    >
      <SettingsToggleSection
        :model-value="callingEnabled"
        :header="$t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE.LABEL')"
        :description="$t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE.DESCRIPTION')"
        :hide-toggle="isTogglingCalling"
        @update:model-value="handleCallingToggle"
      >
        <template v-if="isTogglingCalling" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>

    <template v-if="callingEnabled">
      <div
        class="relative"
        :class="{ 'pointer-events-none opacity-60': isTogglingInbound }"
      >
        <SettingsToggleSection
          :model-value="inboundCallsEnabled"
          :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.LABEL')"
          :description="
            $t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.DESCRIPTION')
          "
          :hide-toggle="isTogglingInbound"
          @update:model-value="handleInboundToggle"
        >
          <template v-if="isTogglingInbound" #hiddenToggle>
            <Spinner class="size-4 text-n-slate-11" />
          </template>
        </SettingsToggleSection>
      </div>

      <CallRecordingSettings :inbox="inbox" />

      <SettingsFieldSection
        v-if="phoneNumber"
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.PHONE_NUMBER.LABEL')"
        :help-text="$t('INBOX_MGMT.WHATSAPP_CALLING.PHONE_NUMBER.HELP_TEXT')"
      >
        <woot-code :script="phoneNumber" lang="html" />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.LABEL')"
        :help-text="
          $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.HELP_TEXT')
        "
      >
        <TextArea
          v-model="permissionRequestBody"
          :placeholder="
            $t(
              'INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.PLACEHOLDER'
            )
          "
          auto-height
          resize
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALL_ICON_LABEL')"
        :help-text="
          $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALL_ICON_HELP_TEXT')
        "
      >
        <NextSelect
          v-model="form.callIconVisibility"
          :options="callIconVisibilityOptions"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.COUNTRIES_LABEL')"
        :help-text="
          $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.COUNTRIES_HELP_TEXT')
        "
      >
        <TagMultiSelectComboBox
          v-model="form.countries"
          :options="countryOptions"
          :placeholder="
            $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.COUNTRIES_PLACEHOLDER')
          "
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="
          $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALLBACK_PERMISSION_LABEL')
        "
        :help-text="
          $t(
            'INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALLBACK_PERMISSION_HELP_TEXT'
          )
        "
      >
        <NextSelect
          v-model="form.callbackPermissionStatus"
          :options="callbackPermissionOptions"
        />
      </SettingsFieldSection>

      <SettingsToggleSection
        v-model="form.callHoursEnabled"
        :header="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALL_HOURS_LABEL')"
        :description="
          $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.CALL_HOURS_HELP_TEXT')
        "
      />

      <template v-if="form.callHoursEnabled">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.TIMEZONE_LABEL')"
        >
          <NextInput v-model="form.timezone" />
        </SettingsFieldSection>

        <SettingsFieldSection
          :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.SCHEDULE_LABEL')"
          :help-text="
            $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.SCHEDULE_HELP_TEXT')
          "
        >
          <div class="flex flex-col gap-2">
            <div
              v-for="slot in form.schedule"
              :key="slot.day"
              class="flex flex-wrap gap-3 items-center"
            >
              <label
                class="flex gap-2 items-center w-40 text-sm text-n-slate-12"
              >
                <input v-model="slot.enabled" type="checkbox" class="m-0" />
                {{ dayLabel(slot.day) }}
              </label>
              <NextInput
                v-model="slot.openTime"
                type="time"
                :disabled="!slot.enabled"
                class="w-32"
              />
              <NextInput
                v-model="slot.closeTime"
                type="time"
                :disabled="!slot.enabled"
                class="w-32"
              />
            </div>
          </div>
        </SettingsFieldSection>

        <SettingsFieldSection
          :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.HOLIDAYS_LABEL')"
          :help-text="
            $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.HOLIDAYS_HELP_TEXT')
          "
        >
          <div class="flex flex-col gap-3">
            <WhatsAppHolidayRow
              v-for="(holiday, index) in form.holidays"
              :key="index"
              :holiday="holiday"
              @remove="removeHoliday(index)"
            />
            <div>
              <NextButton
                faded
                slate
                sm
                :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ADD_HOLIDAY')"
                @click="addHoliday"
              />
            </div>
          </div>
        </SettingsFieldSection>
      </template>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_LABEL')"
        :help-text="
          $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_HELP_TEXT')
        "
      >
        <div class="flex flex-col gap-4">
          <TagMultiSelectComboBox
            v-model="form.routing.primaryTargets"
            :options="routingTargetOptions"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_PRIMARY')
            "
          />
          <TagMultiSelectComboBox
            v-model="form.routing.fallbackTargets"
            :options="routingTargetOptions"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_FALLBACK')
            "
          />
          <NextSelect
            v-model="form.routing.prioritization"
            :options="prioritizationOptions"
          />
          <NextSelect
            v-model="form.routing.ringMode"
            :options="ringModeOptions"
          />
          <NextInput
            v-model="form.routing.maxRecipients"
            type="number"
            min="0"
            :label="
              $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_MAX_RECIPIENTS')
            "
          />
          <NextInput
            v-if="form.routing.ringMode === 'sequential'"
            v-model="form.routing.ringTimeoutSeconds"
            type="number"
            min="1"
            :label="
              $t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.ROUTING_RING_TIMEOUT')
            "
          />
          <label
            v-for="flag in ELIGIBILITY_FLAGS"
            :key="flag"
            class="flex gap-2 items-center text-sm text-n-slate-12"
          >
            <input
              v-model="form.routing.eligibility[flag]"
              type="checkbox"
              class="m-0"
            />
            {{ eligibilityLabel(flag) }}
          </label>
        </div>
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.HOW_IT_WORKS.LABEL')"
        :help-text="$t('INBOX_MGMT.WHATSAPP_CALLING.HOW_IT_WORKS.DESCRIPTION')"
      />

      <div class="flex gap-2">
        <NextButton
          :is-loading="isUpdating"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          @click="updateCallingSettings"
        />
        <NextButton
          faded
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.RESET')"
          @click="resetForm"
        />
        <NextButton
          faded
          slate
          :is-loading="isFetchingFromMeta"
          :label="$t('INBOX_MGMT.WHATSAPP_CALLING.SETTINGS.FETCH_FROM_META')"
          @click="fetchFromMeta"
        />
      </div>
    </template>
  </div>
</template>
